"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { Loader2 } from "lucide-react"
import { propertyService } from "@/services/api"
import type { PropertyBasicUpdateReqDTO } from "@/types/api"

interface PropertyBasicEditModalProps {
  isOpen: boolean
  onClose: () => void
  propertyId: number
  onSuccess?: () => void
}

interface FormData {
  name: string
  price: string
  bathrooms: string
  roommates: string
  diningrooms: string
  isFeatured: boolean
}

interface FormErrors {
  name?: string
  price?: string
  bathrooms?: string
  roommates?: string
  diningrooms?: string
  general?: string
}

export function PropertyBasicEditModal({ isOpen, onClose, propertyId, onSuccess }: PropertyBasicEditModalProps) {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    price: "",
    bathrooms: "",
    roommates: "",
    diningrooms: "",
    isFeatured: false,
  })

  const [errors, setErrors] = useState<FormErrors>({})
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingData, setIsLoadingData] = useState(false)

  // Load property data when modal opens
  useEffect(() => {
    if (isOpen && propertyId) {
      loadPropertyData()
    }
  }, [isOpen, propertyId])

  const loadPropertyData = async () => {
    setIsLoadingData(true)
    try {
      const property = await propertyService.getPropertyById(propertyId)
      setFormData({
        name: property.name,
        price: property.price.toString(),
        bathrooms: property.bathrooms.toString(),
        roommates: property.roommates.toString(),
        diningrooms: property.diningrooms.toString(),
        isFeatured: property.isFeatured,
      })
      setErrors({})
    } catch (error) {
      console.error("Error loading property:", error)
      setErrors({ general: "Error al cargar los datos de la propiedad" })
    } finally {
      setIsLoadingData(false)
    }
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = "El nombre es requerido"
    }

    const price = Number.parseFloat(formData.price)
    if (!formData.price || isNaN(price) || price <= 0) {
      newErrors.price = "El precio debe ser un número mayor a 0"
    }

    const bathrooms = Number.parseInt(formData.bathrooms)
    if (!formData.bathrooms || isNaN(bathrooms) || bathrooms < 1) {
      newErrors.bathrooms = "Los baños deben ser un número mayor a 0"
    }

    const roommates = Number.parseInt(formData.roommates)
    if (!formData.roommates || isNaN(roommates) || roommates < 1) {
      newErrors.roommates = "Los compañeros deben ser un número mayor a 0"
    }

    const diningrooms = Number.parseInt(formData.diningrooms)
    if (!formData.diningrooms || isNaN(diningrooms) || diningrooms < 1) {
      newErrors.diningrooms = "Los comedores deben ser un número mayor a 0"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsLoading(true)
    setErrors({})

    try {
      const updateData: PropertyBasicUpdateReqDTO = {
        name: formData.name.trim(),
        price: Number.parseFloat(formData.price),
        bathrooms: Number.parseInt(formData.bathrooms),
        roommates: Number.parseInt(formData.roommates),
        diningrooms: Number.parseInt(formData.diningrooms),
        isFeatured: formData.isFeatured,
      }

      await propertyService.updatePropertyBasic(propertyId, updateData)

      onSuccess?.()
      onClose()
    } catch (error) {
      console.error("Error updating property:", error)
      setErrors({
        general: error instanceof Error ? error.message : "Error al actualizar la propiedad",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (field: keyof FormData, value: string | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }))
    }
  }

  const handleClose = () => {
    if (!isLoading) {
      setFormData({
        name: "",
        price: "",
        bathrooms: "",
        roommates: "",
        diningrooms: "",
        isFeatured: false,
      })
      setErrors({})
      onClose()
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Editar Propiedad</DialogTitle>
        </DialogHeader>

        {isLoadingData ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span className="ml-2">Cargando datos...</span>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Nombre *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Nombre de la propiedad"
                className={errors.name ? "border-red-500" : ""}
              />
              {errors.name && <p className="text-sm text-red-500">{errors.name}</p>}
            </div>

            {/* Price */}
            <div className="space-y-2">
              <Label htmlFor="price">Precio (€) *</Label>
              <Input
                id="price"
                type="number"
                step="0.01"
                min="0"
                value={formData.price}
                onChange={(e) => handleInputChange("price", e.target.value)}
                placeholder="0.00"
                className={errors.price ? "border-red-500" : ""}
              />
              {errors.price && <p className="text-sm text-red-500">{errors.price}</p>}
            </div>

            {/* Bathrooms */}
            <div className="space-y-2">
              <Label htmlFor="bathrooms">Baños *</Label>
              <Input
                id="bathrooms"
                type="number"
                min="1"
                value={formData.bathrooms}
                onChange={(e) => handleInputChange("bathrooms", e.target.value)}
                placeholder="1"
                className={errors.bathrooms ? "border-red-500" : ""}
              />
              {errors.bathrooms && <p className="text-sm text-red-500">{errors.bathrooms}</p>}
            </div>

            {/* Roommates */}
            <div className="space-y-2">
              <Label htmlFor="roommates">Compañeros *</Label>
              <Input
                id="roommates"
                type="number"
                min="1"
                value={formData.roommates}
                onChange={(e) => handleInputChange("roommates", e.target.value)}
                placeholder="1"
                className={errors.roommates ? "border-red-500" : ""}
              />
              {errors.roommates && <p className="text-sm text-red-500">{errors.roommates}</p>}
            </div>

            {/* Dining Rooms */}
            <div className="space-y-2">
              <Label htmlFor="diningrooms">Comedores *</Label>
              <Input
                id="diningrooms"
                type="number"
                min="1"
                value={formData.diningrooms}
                onChange={(e) => handleInputChange("diningrooms", e.target.value)}
                placeholder="1"
                className={errors.diningrooms ? "border-red-500" : ""}
              />
              {errors.diningrooms && <p className="text-sm text-red-500">{errors.diningrooms}</p>}
            </div>

            {/* Featured */}
            <div className="flex items-center space-x-2">
              <Switch
                id="isFeatured"
                checked={formData.isFeatured}
                onCheckedChange={(checked) => handleInputChange("isFeatured", checked)}
              />
              <Label htmlFor="isFeatured">Propiedad destacada</Label>
            </div>

            {/* General Error */}
            {errors.general && <div className="text-sm text-red-500 bg-red-50 p-3 rounded-md">{errors.general}</div>}

            {/* Actions */}
            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={handleClose} disabled={isLoading}>
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                className="bg-raime-golden-brown hover:bg-raime-golden-brown/90"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Guardando...
                  </>
                ) : (
                  "Guardar Cambios"
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
